/**
 * **S4 — the stage between `attack.ts`'s pre-roll folds and the roll.** The fourth stage of
 * `resolveSingleTarget`'s pipeline: after the cover fold ({@link
 * import("./attackModifiers").foldAttackCover}, S2.5) and the modifier fold ({@link
 * import("./attackModifiers").foldAttackModifiers}, S3), before the attack attempt (S5).
 *
 * - {@link resolveVariableDamageType} (S4) settles a `variable`-typed weapon's concrete damage
 *   type — from a recorded choice, from an attacker-side override, or by returning the prompt.
 *   S5, Invisible's pre-roll coin, is the attack attempt's replacement now
 *   (`events/rollReplacements.ts`'s `missChanceReplacements`).
 *
 * **The coin must be drawn strictly AFTER S4's `needs_decision` early-out**, so a re-resolve that
 * answers the damage-type prompt replays the same coin instead of drawing a fresh one, and strictly
 * BEFORE the d20. `resolveSingleTarget` owns that ordering. S4 CONSTRUCTS its `needs_decision`
 * directly (nothing below it throws one).
 */

import type { Combatant, DamageType, WeaponProfile } from "@lancer-vtt/domain";
import { VARIABLE_DAMAGE_TYPE_OPTIONS, damageTypePromptId, type NeedsDecision } from "./types";

/** What {@link resolveVariableDamageType} settles for the damage blocks further down the spine. */
export type VariableDamageType = {
  /**
   * The attack's **primary** damage type: the attacker-side override if one applies, else the
   * frozen weapon type, else — for a `variable` weapon — the concrete type this attack fires as.
   */
  dtype: DamageType;
  /**
   * The one resolved `variable` choice **every** variable instance reads (primary or rider), or
   * `undefined` when nothing in this attack was `variable` (or an override made the choice moot).
   * Read by the damage-instance loop as `resolvedVariableType ?? dtype`.
   */
  resolvedVariableType: DamageType | undefined;
};

/**
 * **S4** — A2-finish: variable damage-type, resolved BEFORE any dice are drawn (so an
 * unanswered-choice re-resolve doesn't re-roll). A weapon whose damage type is
 * `variable` (the wielder picks the type at fire time) resolves the concrete type via
 * the recorded-choice roundtrip — the same `choose` transport the effects engine uses.
 * A recorded `intent.choices["damage_type:<weaponId>"]` (kinetic/energy/explosive) is
 * honored; in interactive mode an unanswered choice returns `needs_decision` (the
 * prompt round-trip, iterate-pure). Non-interactive (replay / CLI default) falls back
 * to kinetic — the honest deterministic default (the v1 behavior, now choosable).
 *
 * `npc-weapon-rider-damage` Phase 3, decision 9: a RIDER entry typed `variable`
 * inherits this SAME resolved choice — one prompt per attack, never a second one keyed
 * off a rider (RAW's variable-type text is one type choice per attack, not per damage
 * instance). `resolvedVariableType` is the shared resolved value every variable
 * instance (primary or rider) reads; the trigger condition below fires the SAME
 * roundtrip whether it's the primary or a rider that's variable — a concrete primary
 * with a variable rider still resolves via this one promptId. Zero shipping riders are
 * variable (data survey) — this is invariant coverage, not a live-content path.
 * LL0 DSL Primitives III, P2, `override_attack_damage_type`: an ATTACKER-side modifier
 * override wins OUTRIGHT over both the frozen base type and a `variable` weapon's own
 * choose-prompt — `tg_set_damage_type`'s own precedent generalized (see `ModifierContribution
 * .overrideDamageType`'s doc). Gating the whole variable-choice block on its absence means an
 * overridden attack never round-trips a needless "choose the damage type" prompt either.
 *
 * The prompt is routed to `attacker.id` — the wielder picks its own weapon's damage type. This
 * exit constructs its `needs_decision` directly rather than defaulting off a caught signal's
 * `who`, so there is no fallback arm here to get wrong.
 */
export function resolveVariableDamageType(opts: {
  attacker: Combatant;
  weapon: WeaponProfile;
  /** The attacker-side `override_attack_damage_type` facet, if the modifier fold produced one. */
  overrideDamageType: DamageType | undefined;
  /** The intent's recorded choices — where an already-answered prompt is read from. */
  choices: Record<string, string> | undefined;
  /** `effects.interactive` — whether an unanswered choice halts for a prompt or takes the default. */
  interactive: boolean | undefined;
}): VariableDamageType | NeedsDecision {
  const { attacker, weapon, overrideDamageType, choices, interactive } = opts;
  let dtype = overrideDamageType ?? weapon.damage.type;
  let resolvedVariableType: DamageType | undefined;
  if (overrideDamageType === undefined && (dtype === "variable" || (weapon.riders ?? []).some((r) => r.type === "variable"))) {
    const promptId = damageTypePromptId(weapon.id);
    const recorded = choices?.[promptId];
    const dmgOptions = VARIABLE_DAMAGE_TYPE_OPTIONS;
    if (recorded && (dmgOptions as readonly string[]).includes(recorded)) {
      resolvedVariableType = recorded as DamageType;
    } else if (interactive) {
      return {
        kind: "needs_decision",
        who: attacker.id,
        promptId,
        prompt: `Choose ${weapon.name}'s damage type`,
        options: dmgOptions.map((o) => ({ id: o, label: o })),
      };
    } else {
      resolvedVariableType = "kinetic"; // deterministic default (replay / CLI) when no choice is recorded.
    }
    if (dtype === "variable") dtype = resolvedVariableType;
  }
  return { dtype, resolvedVariableType };
}
